import { useEffect, useState, useCallback } from "react";
import { InsertPropertyInfo } from "../../Services/chatbot";
import { toast } from "react-toastify";
import { DEFAULT_WIDGET_CONFIG, SECTIONS } from "./constants";
import TemplateLibrary from "./sections/TemplateLibrary";
import AppearanceSettings from "./sections/AppearanceSettings";
import LivePreview from "./LivePreview";
import { Palette } from "lucide-react";
import { PageHeader } from "@/components/admin/cards";
import "./widgetCustomization.css";

const WidgetCustomization = ({
  setPublishTrigger,
  loadInitialData,
  data,
  isDirty,
  setIsDirty,
  setIsNeedPublish,
}: any) => {
  const [formData, setFormData] = useState<any>(null);
  const [widgetConfig, setWidgetConfig] = useState(DEFAULT_WIDGET_CONFIG);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("templates");

  // Merge existing theme_config colors into widget_config.appearance for backward compatibility
  useEffect(() => {
    if (!data) return;
    const { propertyID, ...sanitizedData } = data;

    const existingWidget = data.widget_config || {};
    const existingTheme = data.theme_config || {};

    const defaultTC = DEFAULT_WIDGET_CONFIG.templateContent || {};
    const existingTC = existingWidget.templateContent || {};
    const mergedTemplateContent: Record<string, any> = {};
    for (const key of Object.keys(defaultTC)) {
      if (existingTC[key]) {
        // User has saved data for this template — start from defaults,
        // then apply saved values, but preserve explicit empty values
        // (empty strings, empty arrays) instead of falling back to defaults.
        const base = { ...defaultTC[key] };
        const saved = existingTC[key];
        for (const field of Object.keys(base)) {
          if (field in saved) {
            base[field] = saved[field];
          }
        }
        // Also include any extra fields from saved data not in defaults
        for (const field of Object.keys(saved)) {
          if (!(field in base)) {
            base[field] = saved[field];
          }
        }
        mergedTemplateContent[key] = base;
      } else {
        mergedTemplateContent[key] = { ...defaultTC[key] };
      }
    }
    for (const key of Object.keys(existingTC)) {
      if (!mergedTemplateContent[key]) {
        mergedTemplateContent[key] = existingTC[key];
      }
    }

    const mergedChatTheme = {
      ...DEFAULT_WIDGET_CONFIG.chatTheme,
      ...existingTheme,
      ...(existingWidget.chatTheme || {}),
    };

    const mergedConfig = {
      ...DEFAULT_WIDGET_CONFIG,
      ...existingWidget,
      appearance: {
        ...DEFAULT_WIDGET_CONFIG.appearance,
        ...(existingTheme.chatprimaryBg && { primaryColor: existingTheme.chatprimaryBg }),
        ...(existingTheme.chatprimaryText && { textColor: existingTheme.chatprimaryText }),
        ...(existingWidget.appearance || {}),
      },
      chatTheme: mergedChatTheme,
      templateContent: mergedTemplateContent,
    };

    setWidgetConfig(mergedConfig);
    setFormData(sanitizedData);
  }, [data]);

  const handleSectionChange = useCallback(
    (sectionKey, value) => {
      setWidgetConfig((prev) => ({
        ...prev,
        [sectionKey]: value,
      }));
      setIsDirty(true);
    },
    [setIsDirty]
  );

  const handleApplyPreset = useCallback(
    (presetChatTheme) => {
      if (!presetChatTheme) return;
      setWidgetConfig((prev) => ({
        ...prev,
        chatTheme: {
          ...prev.chatTheme,
          ...presetChatTheme,
        },
      }));
      setIsDirty(true);
    },
    [setIsDirty]
  );

  const handleSave = async () => {
    if (!formData) return;
    setLoading(true);
    try {
      // Use explicit chatTheme colors for theme_config (backward-compatible)
      const defaultChatTheme = DEFAULT_WIDGET_CONFIG.chatTheme;
      const themeConfig = {
        chatprimaryBg: widgetConfig.chatTheme?.chatprimaryBg || defaultChatTheme.chatprimaryBg,
        chatprimaryText: widgetConfig.chatTheme?.chatprimaryText || defaultChatTheme.chatprimaryText,
        submenuBg: widgetConfig.chatTheme?.submenuBg || defaultChatTheme.submenuBg,
        submenuText: widgetConfig.chatTheme?.submenuText || defaultChatTheme.submenuText,
        submenuHoverBg: widgetConfig.chatTheme?.submenuHoverBg || defaultChatTheme.submenuHoverBg,
        botBg: widgetConfig.chatTheme?.botBg || defaultChatTheme.botBg,
        botText: widgetConfig.chatTheme?.botText || defaultChatTheme.botText,
        chatBgColor: widgetConfig.chatTheme?.chatBgColor || defaultChatTheme.chatBgColor,
        logoBgColor: widgetConfig.chatTheme?.logoBgColor || defaultChatTheme.logoBgColor,
      };

      // Persist widget_config keys needed by the chatbot
      const {
        multiLanguage: _lang,
        welcome: _welcome,
        proactive: _proactive,
        behavior: _behavior,
        deviceVisibility: _device,
        branding: _branding,
        ...savedWidgetConfig
      }: any = widgetConfig;

      // Only save the active template's content; preserve all field values
      // including empty strings and empty arrays so defaults don't creep back in.
      const activeTemplate = savedWidgetConfig.template || "floating";
      const fullTemplateContent = savedWidgetConfig.templateContent || {};
      const activeContent = fullTemplateContent[activeTemplate] || {};
      // Ensure array fields are explicitly set (even when empty) so the
      // backend doesn't strip them and cause defaults to reappear on reload.
      const cleanedContent = { ...activeContent };
      for (const [k, v] of Object.entries(cleanedContent)) {
        if (v === undefined) {
          cleanedContent[k] = Array.isArray((DEFAULT_WIDGET_CONFIG.templateContent?.[activeTemplate] || {})[k])
            ? []
            : "";
        }
      }
      savedWidgetConfig.templateContent = {
        [activeTemplate]: cleanedContent,
      };

      const payload = {
        ...formData,
        theme_config: themeConfig,
        widget_config: savedWidgetConfig,
      };

      const res = await InsertPropertyInfo(payload);
      if (res.status === 200) {
        setIsNeedPublish(true);
        setPublishTrigger((prev) => prev + 1);
        toast.success("Widget customization saved!");
        setIsDirty(false);
        await loadInitialData();
      } else {
        toast.error(res?.data?.message || "Failed to save customization");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.");
      } else {
        toast.error("An error occurred while saving.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!formData) return <div>Loading...</div>;

  const renderSection = () => {
    switch (activeSection) {
      case "templates":
        return <TemplateLibrary config={widgetConfig} onChange={handleSectionChange} />;
      case "appearance":
        return (
          <AppearanceSettings
            config={widgetConfig}
            onChange={handleSectionChange}
            onApplyPreset={handleApplyPreset}
          />
        );
      default:
        return <TemplateLibrary config={widgetConfig} onChange={handleSectionChange} />;
    }
  };

  return (
    <div className="wc-root position-relative">
      {loading && (
        <div className="loaderOverlay zIndex-1023">
          <div className="loading">
            <div className="main-loader" />
          </div>
        </div>
      )}

      <div className={loading ? "blur-sm pointer-events-none" : ""}>
        <PageHeader id="customisationHeading" icon={<Palette />} title="Customisation"
          className="mb-3" description="How your chatbot looks and what it says to visitors, with a live preview." />

        {/* Horizontal Tab Nav */}
        <div className="wc-tab-nav">
          {SECTIONS.map((s) => (
            <button type="button"
              key={s.id}
              className={`wc-section-nav-item ${activeSection === s.id ? "wc-section-nav-item--active" : ""}`}
              onClick={() => setActiveSection(s.id)}
            >
              <i className={`bi ${s.icon}`} />
              <span>{s.label}</span>
            </button>
          ))}
          {isDirty && (
            <button type="button" className="btn btn-primary wc-save-btn" onClick={handleSave}>
              <i className="bi bi-check-lg me-1" />
              Save Changes
            </button>
          )}
        </div>

        <div className="wc-layout">
          {/* Left: Section Content */}
          <div className="wc-content">{renderSection()}</div>

          {/* Right: Live Preview */}
          <div className="wc-preview-panel">
            <LivePreview config={widgetConfig} chatIcon={data?.chaticon} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetCustomization;
